// Dialog de criação de marca — abre POR CIMA do dialog de troca de marca, que
// fica visível atrás. Antes isto era um formulário inline dentro da lista, onde
// se perdia entre as marcas; separado, a criação tem o próprio foco e o próprio
// título.
//
// Este componente não conhece a API: recebe `onSubmit` e só cuida do campo, da
// validação de tamanho e do estado do botão. Quem cria a marca, troca para ela
// e invalida os caches é o BrandDialog — a criação continua sendo um passo do
// fluxo de troca, não uma ilha.
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import "./i18n.js";

export function BrandCreateDialog({
  open,
  onOpenChange,
  busy,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  // Alguma operação em voo no dialog de trás (troca, reativação, exclusão) ou a
  // própria criação: em qualquer um dos casos os controles ficam travados.
  busy: boolean;
  onSubmit: (name: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  // O mesmo piso que o back aplica (CreateBrandBody, minLength 2): sem isso o
  // envio só descobriria o erro no 400.
  const valido = name.trim().length >= 2;

  function fechar() {
    setName("");
    onOpenChange(false);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || !valido) return;
    await onSubmit(name.trim());
    // Sucesso fecha o dialog pelo `open` do pai (a marca criada vira a ativa);
    // erro mantém o que foi digitado para o usuário tentar de novo.
    setName("");
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(aberto) => {
        if (!aberto && busy) return;
        if (!aberto) fechar();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("brandDialog.createTitle")}</DialogTitle>
          <DialogDescription>{t("brandDialog.createDescription")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="nova-marca">{t("brandDialog.createName")}</Label>
            <Input
              id="nova-marca"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("brandDialog.createPlaceholder")}
              autoComplete="organization"
              autoFocus
              disabled={busy}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" disabled={busy} onClick={fechar}>
              {t("brandDialog.cancel")}
            </Button>
            <Button type="submit" disabled={!valido || busy}>
              {busy ? <Spinner className="size-4" /> : <Plus className="size-4" />}
              {busy ? t("brandDialog.creating") : t("brandDialog.createSubmit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
